import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ServiceordreFilter } from '../../aftale/models';
import { ServiceordreStateWithDisplayTextDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-state-with-display-text-dto';
import { Params, Router, ActivatedRoute } from '@angular/router';
import { ProduktService, ServiceordreStateService } from 'src/app/api/services';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { PartnerBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-base-dto';
import { PartnerService } from 'src/app/api/services/partner.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { OpgaveTypeDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opgave-type-dto';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { DateFilterDialogComponent } from '../../shared/date-filter-dialog/date-filter-dialog.component';
import { DateFilterDialogModel } from '../../shared/date-filter-dialog/date-filter-dialog.model';
import moment from 'moment';
import { ProduktDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/produkt-dto';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';

@Component({
  selector: 'app-ordre-filter',
  templateUrl: './ordre-filter.component.html',
  styleUrls: ['./ordre-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OrdreFilterComponent implements OnInit, OnDestroy {
  caller: string = 'OK.Egis.Portal';
  products: ProduktDto[] = [];
  serviceOrderStates: ServiceordreStateWithDisplayTextDto[] = [];
  opgaver: OpgaveTypeDto[] = [
    { id: 10, name: 'Eftersyn' },
    { id: 20, name: 'Tilkald' },
    { id: 40, name: 'Opstart' },
  ];
  partners: PartnerBaseDto[];

  isLoading: boolean = false;

  @Input() filter: ServiceordreFilter;

  unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private serviceordreState: ServiceordreStateService,
    private partnerService: PartnerService,
    private productService: ProduktService,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private localStorage: LocalStorageService,
    private matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    let loaderRef = this.loader.createLoader();
    loaderRef.showStateChanged$.subscribe((shouldShow) => {
      this.isLoading = shouldShow;
    });

    this.changeFilterParams();
    loaderRef.start();
    forkJoin(
      this.productService.getProdukter({
        caller: EGIS_SERVICE_CALLER,
      }),
      this.serviceordreState.getAllServiceOrdreStates({ caller: EGIS_SERVICE_CALLER }),
      this.partnerService.getAllPartnere({
        caller: EGIS_SERVICE_CALLER,
      }),
    )
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          loaderRef.stop();
        }),
      )
      .subscribe(
        (response) => {
          this.products = response[0];
          this.serviceOrderStates = response[1].filter((x) => x.id != 13);
          this.partners = response[2];
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  get hasFilters(): boolean {
    return (
      this.filter.serviceOrderStateIds.length > 0 ||
      this.filter.opgaveIds.length > 0 ||
      this.filter.partnerNos.length > 0 ||
      this.filter.productIds.length > 0
    );
  }

  get hasDateFilters(): boolean {
    return this.filter.periodFrom !== null || this.filter.periodTo !== null;
  }

  filterCount() {
    let filterCount = this.filter.serviceOrderStateIds.length + this.filter.opgaveIds.length + this.filter.partnerNos.length;
    if (this.filter.showParked) {
      filterCount = filterCount + 1;
    }
    return filterCount;
  }

  onStateToggle(id: number) {
    this.toggleValue(this.filter.serviceOrderStateIds, id);
  }

  onOpgaveToggle(id: number) {
    this.toggleValue(this.filter.opgaveIds, id);
  }

  onProductToggle(id: ProduktEnum) {
    this.toggleValue(this.filter.productIds, id);
  }

  toggleValue(array: any[], id: any) {
    if (array.indexOf(id) > -1) {
      array.splice(array.indexOf(id), 1);
    } else {
      array.push(id);
    }
    this.changeFilterParams();
  }

  isStateChecked(id: number) {
    return this.filter.serviceOrderStateIds.indexOf(id) > -1;
  }

  isOpgaveChecked(id: number) {
    return this.filter.opgaveIds.indexOf(id) > -1;
  }

  isProductChecked(id: ProduktEnum) {
    return this.filter.productIds.indexOf(id) > -1;
  }

  changeFilterParams() {
    const uniqueServiceOrderStateIds = this.filter.serviceOrderStateIds.filter((v, i, a) => a.indexOf(v) === i);
    const uniqueOpgaveIds = this.filter.opgaveIds.filter((v, i, a) => a.indexOf(v) === i);
    const uniquePartnerNos = this.filter.partnerNos.filter((v, i, a) => a.indexOf(v) === i);
    const uniqueProductIds = this.filter.productIds.filter((v, i, a) => a.indexOf(v) === i);

    this.localStorage.set('ordre-filter', this.filter);

    const queryParams: Params = {
      serviceOrderStateIds: this.filter.serviceOrderStateIds.length > 0 ? uniqueServiceOrderStateIds : null,
      opgaveIds: this.filter.opgaveIds.length > 0 ? uniqueOpgaveIds : null,
      productIds: this.filter.productIds.length > 0 ? uniqueProductIds : null,
      partnerNos: this.filter.partnerNos.length > 0 ? uniquePartnerNos : null,
      showParked: this.filter.showParked,
      periodFrom: this.filter.periodFrom !== null ? moment(this.filter.periodFrom).format('YYYY-MM-DD') : null,
      periodTo: this.filter.periodTo !== null ? moment(this.filter.periodTo).format('YYYY-MM-DD') : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  reset() {
    this.filter = {
      productIds: [],
      serviceOrderStateIds: [],
      opgaveIds: [],
      partnerNos: [],
      periodFrom: null,
      periodTo: null,
    };
    this.changeFilterParams();
  }

  onPartnerChange(partnerNos: number[]) {
    this.filter.partnerNos = partnerNos;
    this.changeFilterParams();
  }

  onParkedToggle() {
    this.filter.showParked = !this.filter.showParked;
    this.changeFilterParams();
  }

  showDateFilter() {
    const dateFilter = this.matDialog.open<DateFilterDialogComponent>(DateFilterDialogComponent, { data: this.filter });
    dateFilter.afterClosed().subscribe((p: DateFilterDialogModel) => {
      if (p) {
        this.filter.periodFrom = p.periodFrom;
        this.filter.periodTo = p.periodTo;
        this.changeFilterParams();
      }
    });
  }
}
